import {
  mergeRoleDetailFields,
  normalizeEnds,
  unwrap,
  type ApproverItem,
  type DataScope,
  type PermissionNode,
  type PermissionVersionLog,
  type SysRole,
  type UserEnd,
} from '@/core';
import { http } from './http';

/** Auth/Identity — 角色权限：GET /roles、GET /permissions/tree、POST /roles/{id}/permissions、版本与回滚 */

/** 归一化：兼容裸数组和分页壳两种返回格式 */
function normalizeArray<T>(response: unknown): T[] {
  if (Array.isArray(response)) return response;
  if (response && typeof response === 'object') {
    const obj = response as Record<string, unknown>;
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (Array.isArray(obj.list)) return obj.list as T[];
    if (Array.isArray(obj.tree)) return obj.tree as T[];
  }
  return [];
}

/**
 * `GET /roles` 的两档口径（m00153 船长裁定的最终契约）：缺省＝全量＝原行为（5196／BPO 端与用户管理页
 * 的角色字典都走这一档）；`'platform'` ＝收窄后的平台侧两枚（超管后台「平台权限管理 → 角色权限」列表）。
 * ⛔ `'all'` 不往查询串里发：缺省本就是全量，多发一枚参数只会把原行为变成一个没人要的新档位。
 */
export type RoleListScope = 'platform' | 'all';

/** 只有 platform 档带查询参；缺省与 all 一律裸形调用（原行为逐字不变） */
function roleListQuery(scope?: RoleListScope): { params: { scope: 'platform' } } | undefined {
  return scope === 'platform' ? { params: { scope: 'platform' } } : undefined;
}

/** seq322 §六：`sys_role.end_binding` 是 `user_end[]` 的投影，权威值为大写，入口归一一次 */
export function fetchRoles(scope?: RoleListScope): Promise<SysRole[]> {
  return unwrap<unknown>(http.get<unknown>('/roles', roleListQuery(scope))).then((response) =>
    normalizeArray<SysRole>(response).map((role) => ({ ...role, endBinding: normalizeEnds(role.endBinding) })),
  );
}

/**
 * 单角色详情口（GET /roles/{id}，off-contract 登记）出参：含列表口**不投**的三枚
 * （数据范围／端绑定／外部身份）与权限码数组。
 */
export interface RoleDetail {
  id: string;
  name?: string;
  code?: string;
  description?: string | null;
  permissions?: string[];
  dataScope?: string | null;
  endBinding?: string[] | null;
  builtIn?: boolean;
  isExternal?: boolean;
  permVersion?: number;
  status?: string;
  userCount?: number;
}

export function fetchRoleDetail(roleId: string): Promise<RoleDetail> {
  return unwrap<RoleDetail>(http.get<RoleDetail>(`/roles/${roleId}`));
}

/**
 * 角色行补齐：列表口不投那三枚 ⇒ 逐枚走**单角色详情口**补上（角色是运营主数据，枚数有界）。
 *
 * 为什么不把三枚加进列表口：那是契约面 additive 改动，要动契约与棘轮；而详情口本就投这三枚，
 * 前端补一层取数即可。补不到的那一枚原样留着（字段仍 undefined），⛔ 不拿缺省值顶。
 */
export async function fetchRolesWithDetail(scope?: RoleListScope): Promise<SysRole[]> {
  const rows = await fetchRoles(scope);
  const details = await Promise.allSettled(rows.map((row) => fetchRoleDetail(row.id)));
  return rows.map((row, index) => {
    const settled = details[index];
    const merged = mergeRoleDetailFields(row, settled?.status === 'fulfilled' ? settled.value : null);
    return { ...merged, endBinding: normalizeEnds(merged.endBinding) } as SysRole;
  });
}

export function fetchPermissionTree(): Promise<PermissionNode[]> {
  return unwrap<unknown>(http.get<unknown>('/permissions/tree')).then((response) =>
    normalizeArray<PermissionNode>(response),
  );
}

/**
 * `POST /roles` 的请求体＝定义件 **v0.4.1** `components.schemas.RoleCreateRequest`（后端 seq1013 欠项①
 * 已在此代补形：`requestBody` 由 `null` 改成 `$ref` 该枚）。这里只落 TS 形、⛔ 抄第二份字段定义
 * （同源双真口，RK19），值域沿用仓内 `DataScope`／`UserEnd` 两枚联合类型。
 *
 * 三条必须记住的契约事实（逐字取自该枚 schema 的 `description`）：
 * 1. **必现三枚** `name`（1—64）／`code`（1—64，「创建后不可改」）／`dataScope`；
 *    `endBinding`／`inheritFrom`／`permissions` **可选**。
 * 2. `isExternal` **不在入参内**——外部身份由服务端按 `code` 白名单判定，⛔ 由请求方自报；
 *    `endBinding` 缺省＝`[]`（＝不绑端，任何端都进不去）。
 * 3. `permissions` 缺省＝`[]`＝新建角色默认**零权限**，⛔ 从任何「默认模板」隐式继承（否则 AC-ROLE-3
 *    的「关闭」无法复现）；要落权限须走 `POST /roles/{id}/permissions`（那枚才生成版本）。
 *    ⇒ 本函数**不**顺手带权限树勾选，也⛔ 把 `inheritFrom` 当继承用：`RoleDto.x-inherit-note` 写明
 *    首期「只存不展开」，展开会把 `permissions` 变成并集口径。
 *
 * 未知字段策略随该枚 `x-unknown-field-policy`＝**忽略**（与 §3.2 那六枚同侧）。⛔ 照 `approvers`
 * 那枚的「未知字段拒收」写校验器——两条规则不并表（RK19 第 3 条）。
 */
export interface RoleCreatePayload {
  name: string;
  code: string;
  dataScope: DataScope;
  /** 缺省＝`[]`（不绑端）；`ADMIN` 与外部 `code` 相撞由服务端裁 403 `END_BINDING_CONFLICT` */
  endBinding?: UserEnd[];
  /**
   * 定义件＝`integer(int64), nullable`；仓内 `SysRole.inheritFrom` 是**字符串**主键载体
   * （`core/types/models.ts:209`）⇒ 两枚形状今天不一致，属 #72 那批 id 改形的射程。
   * 本枚按**定义件**收（入参面以契约为准），⛔ 为了跟仓内类型对齐而把入参改成 string。
   */
  inheritFrom?: number | null;
  permissions?: string[];
}

/**
 * 新建角色（浮层 `U-ADM-F26`）。成功支＝**200 走信封**、`data: RoleDto`，`permVersion` 起算 1；
 * ⛔ 期待 201（信封约定 §6.0-④）。形状归一沿用 `fetchRoles` 同一手法：`endBinding` 在入口 `normalizeEnds`
 * 一次，免得「同一个角色在列表里大写、在刚建出来的那一格小写」。
 *
 * ⚠ target 写**不带 `/api`** 的 `/roles`：前缀只由 base 提供一次（`api/http.ts` 那张口径表；`d3f08f4` 起
 * base 已带 `/api`）。全仓最后那枚自带前缀的例外 `POST /api/approvers/query` 已于 2026-09-17 改回裸形——
 * 它当时是为了补 base 不带前缀的空缺，前提没了之后只剩双前缀（实测 `/api/api/approvers/query` ⇒ 404）。
 * ⛔ 在这里开第二枚例外。
 */
export function createRole(payload: RoleCreatePayload): Promise<SysRole> {
  return unwrap<SysRole>(http.post<SysRole>('/roles', payload)).then((role) => ({
    ...role,
    endBinding: normalizeEnds(role.endBinding),
  }));
}

export function updateRole(roleId: string, payload: Partial<RoleCreatePayload>): Promise<SysRole> {
  return unwrap<SysRole>(http.post<SysRole>(`/roles/${roleId}`, payload)).then((role) => ({
    ...role,
    endBinding: normalizeEnds(role.endBinding),
  }));
}

/**
 * 保存角色权限的载荷。三枚键都对应服务端请求体里真实存在的成员：
 * `permissions`／`dataScope` ⇒ 该角色下账号的权限集与数据范围；
 * `reason` ⇒ 变更说明，原样进审计明细（服务端读的就是这一位）。
 * ⛔ 键名保持 `reason`：屏上那一格可以叫「变更说明」，但请求体里没有 changeNote 这一位，
 * 换个名字发出去会被静默忽略，审计里就不会留下变更理由。
 * ⛔ 本口不收端绑定：endBinding 不在这个请求体里，塞进来同样被静默忽略
 * （端绑定只在角色新建/编辑的入参面上）。
 */
export interface SavePermissionsPayload {
  permissions: string[];
  dataScope: string;
  reason: string;
}

/**
 * 保存角色权限。回执 permVersion 是该角色行的权限版本号（权限集实变时才递增）。
 * ⛔ 本口不追加权限版本快照行 ⇒ 这次保存不会出现在变更日志里、也无法从变更日志回滚；
 * 本次变更唯一的留痕＝审计明细里的 reason。
 */
export function saveRolePermissions(roleId: string, payload: SavePermissionsPayload): Promise<{ permVersion: number; logId: string }> {
  return unwrap<{ permVersion: number; logId: string }>(http.post(`/roles/${roleId}/permissions`, payload));
}

/**
 * 角色权限矩阵读口的响应形状。`permissions`＝该角色当前生效的权限码全集，
 * `dataScope`＝当前数据范围，`tree`＝带勾选位的矩阵树。
 * ⛔ 别拿角色列表行上的同名字段当它——列表行不投这两枚（本屏原先的空树就是这么来的）。
 */
export interface RoleMatrixNode {
  code: string;
  name: string;
  type: string;
  sortOrder: number;
  selected?: boolean | null;
  children?: RoleMatrixNode[];
}

export interface RolePermissionMatrix {
  roleId: number | string;
  code: string;
  permVersion: number;
  permissions: string[];
  menus: string[];
  dataScope: string;
  readonly: boolean;
  tree: RoleMatrixNode[];
}

export function fetchRolePermissions(roleId: string): Promise<RolePermissionMatrix> {
  return unwrap<RolePermissionMatrix>(http.get<RolePermissionMatrix>(`/roles/${roleId}/permissions`));
}

export function fetchPermissionVersions(roleId: string): Promise<PermissionVersionLog[]> {
  return unwrap<unknown>(http.get<unknown>('/permission-versions', { params: { roleId } })).then((response) =>
    normalizeArray<PermissionVersionLog>(response),
  );
}

export function rollbackPermissionVersion(versionId: string, reason: string): Promise<{ permVersion: number }> {
  return unwrap<{ permVersion: number }>(http.post(`/permission-versions/${versionId}/rollback`, { reason }));
}

/**
 * 导出审批人候选 —— 步骤 3 定义件 **§6.2** `POST /api/approvers/query`。
 *
 * ⚠ **本枚曾是全仓唯一按完整路径写的 target，例外已于 2026-09-17 改回**（改回记录在下面，⛔ 再抄回完整路径）：
 * 后端 seq761 三① 登记时只有这枚字面量自带 `/api`、组前缀为零（`ContractGapModule.cs:95`），而 §6.0-①
 * 定的 base 不带 `/api` ⇒ 当时我选 **甲（本枚按完整路径写）**。甲的代价是同一个字面量同时活在两处
 * （api 层＋桩表），所以 `apiPaths.assert.ts` 为它留了三臂尺。
 *
 * **改回依据**：#81 的 `/api` 前缀已在线上装载。实测尺名＝「前缀统一实测」（2026-09-17 · 本机 8080 ·
 * 线上容器 1381780）：`/orders`、`/auth/profile`、`/regions`、`/users`、`/alert-rules` 五枚**裸形全 404**，
 * 同五枚**带 `/api` 形全 200**；而 `POST /api/approvers/query` 200、`/approvers/query` 404 ⇒ 该枚
 * 也已在统一前缀下。base 自 d3f08f4 起就是 `/api`（群主 seq=181 裁定保留），甲案字面量与它拼出的
 * `/api/api/approvers/query` 实测 404 —— 例外一失效就从「补形」变成「双前缀缺陷」，故按甲案原计划改回。
 * **同批改动面**（三处必须一起动，少一处就是一边改一边没改）：本枚 target 字面量、`src/mock/index.ts`
 * 的桩表键、`apiPaths.assert.ts` 的三臂（完整路径形应 0 处、普通形恰 2 处、⛔ 第二处 `/api` 例外）。
 * ⛔ 拿这枚当"新端点都带 `/api`"的先例——它现在恰恰是"⛔ 再写第二枚自带前缀字面量"的反面教材。
 *
 * 改指依据：§8.2「§6 两枚按目标定义直接写，联调时不需要二次改形」⇒ 旧
 * `GET /users?perm=export:approve`（分页壳）就此停用，不再作为名册的数据源。
 *
 * 三条必须记住的契约事实：
 * 1. 请求体＝顶层对象 ＋ **恰好一枚键** `perm`（Ordinal 全文等值、值 ∈ `{"export:approve"}`）；
 *    多一枚键、键名大小写不符、值非字符串**一律 400 走信封**——这里未知字段是**拒收**的，
 *    与 §6.1 那六枚的「忽略」不并表（RK19 第 3 条）。所以本函数刻意不做任何参数展开。
 * 2. 成功支 `data` 是**裸数组** `ApproverItem[]`（无 `items/page/size/total` 壳）——见 §4.2 的显式例外。
 * 3. 未带令牌 → 401；除此之外**不因身份或数据范围产生 403**（AC-ROLE-7 第 1 条），
 *    区域管理员在这里拿到的应当是名单本身，而不是 `DATA_SCOPE_DENIED`。
 *
 * 沿用旧口径：后端已排除 `status = DISABLED` 的用户，调用方**不得**再过滤一次
 * （否则「返回 N 条、下拉只显示 M 条」）。后端 seq761 四-2 另实测：那枚排除今天**只由夹具成员成立、
 * 不由谓词成立**（`ContractGapModule.cs:486-488` 无 `Where`）⇒ 修法在服务端，且 `ApproverItemDto`
 * 恰三键、多一枚 `status` 直接撞 PRD `:59`「多一个即不通过」⇒ 前端 ⛔ 自查、⛔ 请后端回显 `status`。
 */
export function fetchExportApprovers(): Promise<ApproverItem[]> {
  return unwrap<ApproverItem[]>(http.post<ApproverItem[]>('/approvers/query', { perm: 'export:approve' }));
}
